import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Slider,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const STATE = {
    IDLE: 'idle',          // Publishing silence on the persistent connection
    CONNECTING: 'connecting',
    TALKING: 'talking',
};
const WHIP_PATH = '/videortc/';
const getCsrfToken = () =>
    document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') ?? '';

// This client sends a complete offer instead of using WHIP trickle-ICE PATCH.
const waitForPeer = (pc, event, ready, timeoutMs) => new Promise((resolve, reject) => {
    const done = (error) => {
        clearTimeout(timer);
        pc.removeEventListener(event, check);
        error ? reject(error) : resolve();
    };
    const check = () => {
        if (pc.signalingState === 'closed' || pc.connectionState === 'failed') {
            done(new Error('Browser audio connection failed.'));
        } else if (ready()) done();
    };
    const timer = setTimeout(() => done(new Error('Browser audio connection timed out.')), timeoutMs);
    pc.addEventListener(event, check);
    check();
});

/** Persistent silent WHIP publisher. PTT only replaces its sender's track. */
const HornTalkComponent = ({ toHornStream, revision, onError, onPublisherReady, onRequestIntent, closePublisherRef, resetIntentRef, onStatusChange, embedded = false, disabled = false }) => {
    const [talkState, setTalkState] = useState(STATE.IDLE);
    const [publisherState, setPublisherState] = useState('connecting');
    const [needsInteraction, setNeedsInteraction] = useState(false);
    const [intentState, setIntentState] = useState('idle');
    const [errorMsg, setErrorMsg] = useState('');
    const [devices, setDevices] = useState([]);
    const [selectedDeviceId, setSelectedDeviceId] = useState('');
    const [gain, setGain] = useState(100);
    const gainRef = useRef(100);
    const isPressingRef = useRef(false);
    const isKeyHeldRef = useRef(false);
    const talkStateRef = useRef(STATE.IDLE);
    const publisherRef = useRef(null);
    const microphoneRef = useRef(null);
    const mountedRef = useRef(false);
    const pressTokenRef = useRef(0);
    const intentStateRef = useRef('idle');
    const intentTokenRef = useRef(0);
    const intentWaitRef = useRef(null);
    const callbacksRef = useRef({ onError, onPublisherReady });
    callbacksRef.current = { onError, onPublisherReady };

    const log = useCallback((event, details = {}) => {
        console.info('[HornAudio:Talk]', event, { revision, stream: toHornStream, ...details });
    }, [revision, toHornStream]);

    const setTalkStateSynced = useCallback((state) => {
        talkStateRef.current = state;
        if (mountedRef.current) setTalkState(state);
    }, []);
    const loadAudioDevices = useCallback(async () => {
        try {
            const all = await navigator.mediaDevices.enumerateDevices();
            if (!mountedRef.current) return;
            const inputs = all.filter((device) => device.kind === 'audioinput');
            setDevices(inputs);
            if (inputs.length) setSelectedDeviceId((previous) => previous || inputs[0].deviceId);
        } catch { /* Device labels can remain unavailable until the first press. */ }
    }, []);

    const stopMicrophone = useCallback(() => {
        const mic = microphoneRef.current;
        microphoneRef.current = null;
        if (!mic) return;
        log('microphone:released', { physicalTracks: mic.stream.getTracks().length });
        // Mute synchronously, including while a replaceTrack promise is pending.
        mic.gain && (mic.gain.gain.value = 0);
        mic.stream.getTracks().forEach((track) => { track.enabled = false; track.stop(); });
        mic.track?.stop();
        mic.source?.disconnect();
        mic.gain?.disconnect();
        mic.destination?.disconnect();
        mic.context?.close().catch(() => {});
    }, [log]);

    // Serialize sender mutations. Release stops the microphone immediately; this
    // queue also guarantees that a late microphone replacement ends on silence.
    const replaceTrack = useCallback((publisher, track, valid = () => true) => {
        const operation = publisher.trackQueue.catch(() => {}).then(async () => {
            if (publisher.closed || !valid()) {
                log('track:switch-cancelled', { target: track === publisher.track ? 'silence' : 'microphone' });
                return false;
            }
            if (publisher.sender.track !== track) {
                await publisher.sender.replaceTrack(track);
                log('track:replaced', { target: track === publisher.track ? 'silence' : 'microphone', stillRequested: valid() });
            }
            return !publisher.closed && valid();
        });
        publisher.trackQueue = operation;
        return operation;
    }, [log]);

    const doRelease = useCallback(() => {
        if (isPressingRef.current || microphoneRef.current) log('ptt:release', { publisherKeptOpen: true });
        ++pressTokenRef.current;
        isPressingRef.current = false;
        isKeyHeldRef.current = false;
        stopMicrophone();
        const publisher = publisherRef.current;
        if (publisher && !publisher.closed) {
            replaceTrack(publisher, publisher.track).catch((error) => publisher.fail(error));
        }
        setTalkStateSynced(STATE.IDLE);
    }, [replaceTrack, stopMicrophone, setTalkStateSynced, log]);

    useEffect(() => {
        let active = true;
        let retryTimer;
        let wakeRetry;
        mountedRef.current = true;
        loadAudioDevices();

        const deleteSession = (publisher) => {
            if (!publisher.url) return Promise.resolve();
            if (!publisher.deletion) {
                log('whip:DELETE', { reason: 'publisher teardown' });
                publisher.deletion = fetch(publisher.url, {
                    method: 'DELETE', credentials: 'include', keepalive: true,
                    headers: { 'X-CSRF-TOKEN': getCsrfToken() },
                    signal: AbortSignal.timeout(4000),
                }).then((response) => {
                    log('whip:DELETE-response', { status: response.status });
                }).catch((error) => log('whip:DELETE-failed', { error: error.message }));
            }
            return publisher.deletion;
        };
        const dispose = (publisher) => {
            if (!publisher) return Promise.resolve();
            if (!publisher.closed) {
                log('publisher:closing', { connectionState: publisher.pc.connectionState });
                publisher.closed = true;
                doRelease();
                clearTimeout(publisher.disconnectTimer);
                publisher.pc.close();
                publisher.track.stop();
                publisher.oscillator.stop();
                publisher.oscillator.disconnect();
                publisher.silentGain.disconnect();
                publisher.destination.disconnect();
                publisher.context.onstatechange = null;
                publisher.context.close().catch(() => {});
                publisher.fail(new Error('Publisher closed.'));
            }
            return deleteSession(publisher);
        };
        const close = () => {
            active = false;
            mountedRef.current = false;
            clearTimeout(retryTimer);
            wakeRetry?.();
            const publisher = publisherRef.current;
            const deletion = dispose(publisher);
            // If POST completes after unmount, connect() captures its Location and
            // deletes it before the panel revokes this revision's authorization.
            return Promise.all([deletion, publisher?.initialization?.catch(() => {})]).then(() =>
                publisher && deleteSession(publisher)
            );
        };
        if (closePublisherRef) closePublisherRef.current = { revision, close };

        const connect = async (publisher) => {
            const { pc, context } = publisher;
            const current = () => active && !publisher.closed && publisherRef.current === publisher;
            const reportAudioState = () => {
                if (!current()) return;
                log('silent-audio:state', { state: context.state, requiresInteraction: context.state !== 'running' });
                setNeedsInteraction(context.state !== 'running');
                if (context.state === 'running' && pc.connectionState === 'connected') {
                    log('publisher:ready', { audio: 'silence', microphoneAccessed: false });
                    callbacksRef.current.onPublisherReady?.();
                } else if (context.state !== 'running') {
                    doRelease();
                }
            };
            context.onstatechange = reportAudioState;
            // Autoplay may leave resume() pending until a trusted gesture. WHIP
            // negotiation can proceed, but we explicitly show that media is blocked.
            context.resume().catch(() => {});
            reportAudioState();
            pc.onconnectionstatechange = () => {
                if (!current()) return;
                log('publisher:connection-state', { state: pc.connectionState, iceState: pc.iceConnectionState });
                if (pc.connectionState === 'failed') publisher.fail(new Error('Browser publishing connection lost.'));
                if (pc.connectionState === 'connected') {
                    clearTimeout(publisher.disconnectTimer);
                    publisher.disconnectTimer = null;
                    setPublisherState('connected');
                    reportAudioState();
                } else {
                    doRelease();
                    setPublisherState('connecting');
                    if (pc.connectionState === 'disconnected' && !publisher.disconnectTimer) {
                        publisher.disconnectTimer = setTimeout(() => publisher.fail(new Error('Browser publishing connection lost.')), 10000);
                    }
                }
            };
            await pc.setLocalDescription(await pc.createOffer());
            await waitForPeer(pc, 'icegatheringstatechange', () => pc.iceGatheringState === 'complete', 10000);
            if (!current()) return;
            const endpoint = new URL(WHIP_PATH + encodeURIComponent(toHornStream) + '/whip', window.location.origin);
            endpoint.searchParams.set('horn_revision', revision);
            log('whip:POST', { path: endpoint.pathname });
            const response = await fetch(endpoint.href, {
                method: 'POST',
                headers: { 'Content-Type': 'application/sdp', 'X-CSRF-TOKEN': getCsrfToken() },
                body: pc.localDescription.sdp, credentials: 'include', keepalive: true,
                signal: AbortSignal.timeout(15000),
            });
            const location = response.headers.get('Location');
            log('whip:POST-response', { status: response.status, hasSessionLocation: Boolean(location) });
            if (location) {
                const url = new URL(location, endpoint);
                // Follow only this stream's same-origin WHIP resource through the proxy.
                if (url.origin !== endpoint.origin || !url.pathname.startsWith(endpoint.pathname + '/')) {
                    throw new Error('Invalid WHIP session URL.');
                }
                url.searchParams.set('horn_revision', revision);
                publisher.url = url.href;
            }
            if (!current()) { log('whip:late-response-cleanup'); await deleteSession(publisher); return; }
            if (!response.ok) throw new Error('WHIP response ' + response.status);
            if (!publisher.url) throw new Error('WHIP response missing session Location.');
            await pc.setRemoteDescription({ type: 'answer', sdp: await response.text() });
            await waitForPeer(pc, 'connectionstatechange', () => pc.connectionState === 'connected', 15000);
            if (current()) {
                setPublisherState('connected');
                setErrorMsg('');
                reportAudioState();
            }
        };
        const run = async () => {
            // Initial attempt plus three bounded recoveries; never change the revision
            // or send device audio_start as a side effect of browser recovery.
            for (let attempt = 0; active && attempt < 4; attempt++) {
                let publisher;
                try {
                    log('publisher:initializing', { attempt: attempt + 1, maxAttempts: 4 });
                    setPublisherState('connecting');
                    const context = new AudioContext();
                    const oscillator = context.createOscillator();
                    const silentGain = context.createGain();
                    const destination = context.createMediaStreamDestination();
                    destination.channelCount = 1;
                    silentGain.gain.value = 0;
                    oscillator.connect(silentGain);
                    silentGain.connect(destination);
                    oscillator.start();
                    const pc = new RTCPeerConnection({ iceServers: [], bundlePolicy: 'max-bundle' });
                    publisher = { context, oscillator, silentGain, destination, pc,
                        track: destination.stream.getAudioTracks()[0], trackQueue: Promise.resolve(), closed: false };
                    publisher.failed = new Promise((resolve) => { publisher.fail = resolve; });
                    publisherRef.current = publisher;
                    publisher.sender = pc.addTransceiver(publisher.track, { direction: 'sendonly', streams: [destination.stream] }).sender;
                    publisher.initialization = connect(publisher);
                    await publisher.initialization;
                    if (!active) break;
                    throw await publisher.failed;
                } catch (error) {
                    await dispose(publisher);
                    if (!active) break;
                    log('publisher:failed', { error: error.message, retryInMs: attempt < 3 ? 1000 * (2 ** attempt) : null });
                    setPublisherState('failed');
                    setErrorMsg(attempt < 3 ? 'Browser publisher interrupted. Reconnecting…' : (error.message || 'Could not open the browser publisher.'));
                    if (attempt === 3) { callbacksRef.current.onError?.(error.message); break; }
                    await new Promise((resolve) => {
                        wakeRetry = resolve;
                        retryTimer = setTimeout(resolve, 1000 * (2 ** attempt));
                    });
                }
            }
        };
        run();
        const onPageHide = () => close();
        window.addEventListener('pagehide', onPageHide);
        return () => {
            mountedRef.current = false;
            close();
            window.removeEventListener('pagehide', onPageHide);
        };
    }, [toHornStream, revision, closePublisherRef, doRelease, loadAudioDevices, log]);

    useEffect(() => {
        const onHide = () => doRelease();
        const onVisibility = () => { if (document.visibilityState !== 'visible') doRelease(); };
        window.addEventListener('blur', onHide);
        document.addEventListener('visibilitychange', onVisibility);
        return () => {
            window.removeEventListener('blur', onHide);
            document.removeEventListener('visibilitychange', onVisibility);
        };
    }, [doRelease]);

    const enableAudio = useCallback(() => {
        log('silent-audio:enable-requested');
        publisherRef.current?.context.resume().catch((error) => {
            if (mountedRef.current) setErrorMsg(error.message);
        });
    }, [log]);

    const cancelPendingIntent = useCallback(() => {
        ++intentTokenRef.current;
        if (intentWaitRef.current) {
            clearTimeout(intentWaitRef.current.timer);
            intentWaitRef.current.resolve();
            intentWaitRef.current = null;
        }
    }, []);

    const resetIntent = useCallback(() => {
        cancelPendingIntent();
        doRelease();
        intentStateRef.current = 'idle';
        setIntentState('idle');
        setErrorMsg('');
        log('intent:reset', { publisherKeptOpen: true });
    }, [cancelPendingIntent, doRelease, log]);

    useEffect(() => {
        intentStateRef.current = 'idle';
        setIntentState('idle');
        if (resetIntentRef) resetIntentRef.current = { revision, reset: resetIntent };
        return () => {
            cancelPendingIntent();
            if (resetIntentRef?.current?.reset === resetIntent) resetIntentRef.current = null;
        };
    }, [revision, resetIntentRef, resetIntent, cancelPendingIntent]);

    const requestMicrophoneIntent = useCallback(async () => {
        if (disabled || intentStateRef.current !== 'idle') return;
        const token = ++intentTokenRef.current;
        const current = () => mountedRef.current && token === intentTokenRef.current;
        intentStateRef.current = 'arming';
        setIntentState('arming');
        setErrorMsg('');
        // The initial click can also resume silent audio, without capturing the mic.
        enableAudio();
        log('intent:requesting');
        try {
            if (!onRequestIntent) throw new Error('Microphone activation is unavailable.');
            await onRequestIntent();
            if (!current()) return;
            log('intent:waiting', { delayMs: 1000 });
            await new Promise((resolve) => {
                intentWaitRef.current = { resolve, timer: setTimeout(resolve, 1000) };
            });
            if (!current()) return;
            intentWaitRef.current = null;
            intentStateRef.current = 'ready';
            setIntentState('ready');
            log('intent:ready');
        } catch (error) {
            if (!current()) return;
            intentStateRef.current = 'idle';
            setIntentState('idle');
            setErrorMsg(error.message || 'Could not activate the microphone. Click to retry.');
            log('intent:failed', { error: error.message });
        }
    }, [enableAudio, log, onRequestIntent, disabled]);

    const doPress = useCallback(async () => {
        if (disabled || intentStateRef.current !== 'ready') return;
        if (isPressingRef.current) return;
        const publisher = publisherRef.current;
        if (!publisher || publisher.closed || publisher.pc.connectionState !== 'connected') {
            log('ptt:ignored', { reason: 'publisher not connected' });
            return;
        }
        const token = ++pressTokenRef.current;
        log('ptt:press', { press: token, gainPercent: gainRef.current, microphone: selectedDeviceId ? 'selected input' : 'default input' });
        isPressingRef.current = true;
        setTalkStateSynced(STATE.CONNECTING);
        const valid = () => mountedRef.current && isPressingRef.current && token === pressTokenRef.current
            && publisherRef.current === publisher && !publisher.closed && publisher.pc.connectionState === 'connected';
        try {
            // Invoke resume during the user's gesture, before awaiting microphone permission.
            await publisher.context.resume();
            if (!valid()) return;
            const constraints = { audio: selectedDeviceId ? { deviceId: { exact: selectedDeviceId } } : true };
            log('microphone:requesting', { press: token });
            const stream = await navigator.mediaDevices.getUserMedia(constraints);
            if (!valid()) {
                stream.getTracks().forEach((track) => track.stop());
                log('microphone:late-result-discarded', { press: token });
                return;
            }
            log('microphone:acquired', { press: token });
            const mic = { stream };
            microphoneRef.current = mic;
            mic.context = new AudioContext();
            mic.source = mic.context.createMediaStreamSource(stream);
            mic.gain = mic.context.createGain();
            mic.gain.gain.value = gainRef.current / 100;
            mic.destination = mic.context.createMediaStreamDestination();
            mic.destination.channelCount = 1;
            mic.source.connect(mic.gain);
            mic.gain.connect(mic.destination);
            mic.track = mic.destination.stream.getAudioTracks()[0];
            if (!mic.track) throw new Error('Microphone track not ready.');
            await mic.context.resume();
            if (mic.context.state !== 'running' && valid()) throw new Error('Browser microphone audio is paused.');
            if (!valid()) return; // Release already disposed this microphone.
            const replaced = await replaceTrack(publisher, mic.track, valid);
            if (!replaced || !valid()) return;
            loadAudioDevices();
            log('ptt:talking', { press: token, connectionState: publisher.pc.connectionState });
            setTalkStateSynced(STATE.TALKING);
        } catch (error) {
            if (!valid()) return;
            doRelease();
            log('ptt:failed', { press: token, error: error.message });
            setErrorMsg(error?.message || 'Microphone or browser publishing error.');
            callbacksRef.current.onError?.(error.message);
        }
    }, [selectedDeviceId, replaceTrack, loadAudioDevices, doRelease, setTalkStateSynced, log, disabled]);

    // ── Pointer events ────────────────────────────────────────────────────────

    const handlePointerDown = useCallback((e) => {
        // The first activation is a normal click, separate from hold-to-talk.
        if (intentStateRef.current !== 'ready') return;
        e.preventDefault();
        if (e.currentTarget.setPointerCapture) {
            try { e.currentTarget.setPointerCapture(e.pointerId); } catch {}
        }
        doPress();
    }, [doPress]);

    const handlePointerUp = useCallback((e) => {
        e.preventDefault();
        if (!isPressingRef.current) return;
        doRelease();
    }, [doRelease]);

    const handlePointerCancel = useCallback((e) => {
        e.preventDefault();
        if (!isPressingRef.current) return;
        doRelease();
    }, [doRelease]);

    // ── Keyboard (Space / Enter — hold, suppress auto-repeat) ────────────────

    const handleKeyDown = useCallback((e) => {
        if (e.code !== 'Space' && e.code !== 'Enter') return;
        if (e.repeat) { e.preventDefault(); return; }
        if (intentStateRef.current !== 'ready') return;
        if (isKeyHeldRef.current) return;
        isKeyHeldRef.current = true;
        e.preventDefault();
        doPress();
    }, [doPress]);

    const handleKeyUp = useCallback((e) => {
        if (e.code !== 'Space' && e.code !== 'Enter') return;
        if (intentStateRef.current !== 'ready') return;
        isKeyHeldRef.current = false;
        e.preventDefault();
        if (!isPressingRef.current) return;
        doRelease();
    }, [doRelease]);

    const handleGainChange = useCallback((_, v) => {
        setGain(v);
        gainRef.current = v;
        if (microphoneRef.current?.gain) {
            microphoneRef.current.gain.gain.value = v / 100;
        }
    }, []);

    // ── Render ────────────────────────────────────────────────────────────────

    const dismissError = useCallback(() => setErrorMsg(''), []);
    useEffect(() => {
        onStatusChange?.({ error: errorMsg, needsInteraction, intentState, publisherState, enableAudio, dismissError });
    }, [onStatusChange, errorMsg, needsInteraction, intentState, publisherState, enableAudio, dismissError]);
    useEffect(() => () => onStatusChange?.(null), [onStatusChange]);

    const isTalking = talkState === STATE.TALKING;
    const isArming = intentState === 'arming';
    const isExpanded = intentState !== 'idle';
    const isConnecting = talkState === STATE.CONNECTING || publisherState === 'connecting' || isArming;

    const buttonColor = isTalking ? 'error' : isExpanded ? 'warning' : 'primary';
    const statusText = isArming ? 'Preparing microphone…' : isTalking ? 'Talking…' : isConnecting ? 'Connecting…' : publisherState !== 'connected' ? 'Publisher unavailable' : intentState === 'idle' ? 'Idle' : 'Hold to talk';
    const statusColor = isTalking ? 'error.main' : intentState === 'idle' ? 'text.secondary' : 'warning.main';
    const buttonHint = intentState === 'idle' ? 'Click to enable talking. After one second, press and hold to talk.' : isArming ? 'Preparing the horn speaker…' : 'Hold to talk';

    const permissionHint = (
        <Tooltip title="Click the microphone once to enable talking. Wait one second, then press and hold to speak. Microphone permission is requested only when you hold to talk.">
            <InfoOutlinedIcon fontSize="small" sx={{ verticalAlign: 'middle', ml: 0.5, cursor: 'help', color: 'text.secondary' }} />
        </Tooltip>
    );

    return (
        <Box sx={embedded ? { py: 0.5 } : { p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {!embedded && <span>🎤</span>}
                <span>{embedded ? 'Microphone' : 'Push-to-talk'}</span>
                {permissionHint}
            </Typography>

            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" sx={{ minHeight: isExpanded ? 56 : undefined }}>
                <IconButton
                    color={buttonColor}
                    disabled={disabled || !toHornStream || publisherState !== 'connected' || isArming}
                    onClick={requestMicrophoneIntent}
                    onPointerDown={handlePointerDown}
                    onPointerUp={handlePointerUp}
                    onPointerCancel={handlePointerCancel}
                    onLostPointerCapture={handlePointerCancel}
                    onBlur={doRelease}
                    onKeyDown={handleKeyDown}
                    onKeyUp={handleKeyUp}
                    size={isExpanded ? 'large' : 'small'}
                    sx={{
                        ...(isExpanded ? {
                            width: 48,
                            height: 48,
                            border: '2px solid',
                            borderColor: isTalking ? 'error.main' : 'warning.main',
                        } : {}),
                        touchAction: 'none',
                        userSelect: 'none',
                        WebkitUserSelect: 'none',
                        flexShrink: 0,
                    }}
                    title={buttonHint}
                    aria-label={intentState === 'ready' ? 'Hold to talk' : 'Enable microphone'}
                    aria-pressed={isTalking}
                >
                    {isTalking || intentState === 'idle' ? <MicIcon /> : <MicOffIcon />}
                </IconButton>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0, flexWrap: 'wrap' }}>
                    <Typography
                        variant="body2"
                        color={statusColor}
                        sx={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                    >
                        <span>{statusText}</span>
                        {isConnecting && <CircularProgress size={10} />}
                    </Typography>

                    {isExpanded && devices.length > 1 && (
                        <FormControl size="small" sx={{ minWidth: 160 }}>
                            <InputLabel>Mic</InputLabel>
                            <Select
                                value={selectedDeviceId}
                                label="Mic"
                                onChange={(e) => setSelectedDeviceId(e.target.value)}
                                disabled={isTalking || isConnecting}
                            >
                                {devices.map((d) => (
                                    <MenuItem key={d.deviceId} value={d.deviceId}>
                                        {d.label || 'Microphone ' + (devices.indexOf(d) + 1)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}

                    {isExpanded && (
                        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 150 }}>
                            <MicIcon fontSize="small" color="action" />
                            <Slider
                                size="small"
                                value={gain}
                                onChange={handleGainChange}
                                min={0}
                                max={200}
                                aria-label="Microphone gain"
                                sx={{ width: 90 }}
                            />
                            <Typography variant="caption" sx={{ minWidth: 36 }}>{gain}%</Typography>
                        </Stack>
                    )}
                </Box>
            </Stack>

            {!embedded && intentState === 'idle' && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    Click the microphone to enable talking, then press and hold to speak.
                </Typography>
            )}

            {!embedded && needsInteraction && (
                <Alert severity="info" sx={{ mt: 1 }} action={<Button onClick={enableAudio}>Enable audio</Button>}>
                    Your browser paused the silent audio publisher. Enable audio to keep the horn channel active.
                </Alert>
            )}

            {!embedded && errorMsg && (
                <Alert severity="error" sx={{ mt: 1 }} onClose={() => setErrorMsg('')}>
                    {errorMsg}
                </Alert>
            )}
        </Box>
    );
};

HornTalkComponent.propTypes = {
    toHornStream: PropTypes.string.isRequired,
    revision: PropTypes.string.isRequired,
    onError: PropTypes.func,
    onPublisherReady: PropTypes.func,
    onRequestIntent: PropTypes.func.isRequired,
    closePublisherRef: PropTypes.shape({ current: PropTypes.any }),
    resetIntentRef: PropTypes.shape({ current: PropTypes.any }),
    onStatusChange: PropTypes.func,
    embedded: PropTypes.bool,
    disabled: PropTypes.bool,
};

HornTalkComponent.defaultProps = {
    toHornStream: '',
    onError: null,
};

export default HornTalkComponent;
